// readinessAI.js — morning training readiness classification via Gemini.
//
// generateReadiness(allData, { steveToday }) → writes to readinessCache and
// returns { classification, rationale, nutritionAdvice, keyDrivers, steveToday }.

import { callGemini } from "../geminiAI";
import { COACH_VOICE, EVIDENCE_STANDARD, OUTPUT_JSON_STRICT, composeSystem } from "./hayahPrompts";
import { getUserProfile, formatProfileForPrompt } from "./userProfile";
import { publishSources, readinessSources } from "./pipelineSources";
import { MODELS } from "./aiConfig";
import { computeReadinessInputs } from "./readiness";
import { saveReadinessCache } from "./readinessCache";
import { scoreReadiness } from "./readinessScore";

const READINESS_CRITERIA = `Classify training readiness based on objective data only. Do not consider subjective feel — the user does not report it. Use ALL the signals below in concert, not just HRV+sleep.

POST-WORKOUT OVERRIDE — check this FIRST, before any other classification:
- If training.trainedToday is true, the classification MUST be RECOVERY. The user has already completed their session for the day; the readiness question is no longer "should I train" but "what for the rest of today + tomorrow's outlook". Everything below describes the "no session yet today" path. Skip to RECOVERY when trainedToday=true.

HARD: HRV within 5% of 14d avg AND 3d trend not negative, sleep ≥7h with ≥1.5h deep+REM (or 7d avg ≥7h if today is an outlier), last hard session ≥48h ago, RHR within 3bpm of 14d avg, ACWR in 0.8–1.3 sweet spot.
MEDIUM: HRV within 15% of avg, sleep ≥6h, last hard session ≥24h ago, no major recovery red flags, ACWR ≤1.5.
EASY: HRV down >15% from avg, sleep <6h, OR back-to-back high-volume days, OR RHR elevated >5bpm, OR ACWR >1.5 (acute spike), OR 3d HRV trend down >10%.
REST: HRV down >25% from avg, sleep <5h, OR 5+ consecutive training days, OR 7d sleep debt >7h, OR clear overreaching pattern (HRV down + RHR up + 3d trends both adverse).
RECOVERY: trainedToday=true (any reason — see override above). Rationale and nutritionAdvice answer different questions in this mode (see below).

CONFOUNDER OVERRIDES — apply BEFORE classifying:
- body.overnightDeltaKg < -1.5 → likely dehydration; HRV drop is mechanical, not fatigue. Mention in rationale; do NOT downgrade purely on HRV.
- body.weight7dDeltaKg < -1.0 with low sleep + high active kcal → aggressive deficit suppressing recovery; bias one tier easier.
- training.lastWorkout.maxHr > 170 AND durationMin > 60 AND hoursAgo < 36 → high systemic load even if HRV looks fine; bias one tier easier.
- training.lastWorkout.prCount ≥ 2 AND hoursAgo < 48 → nervous-system spent; bias EASY unless 7d sleep ≥7.5h.
- activity.steps48h > 30000 → passive load on top of training; bias one tier easier for lower-body work.
- mounjaro.dayOfCycle 1-3 → expect appetite/energy suppression; bias one tier easier on strength.
- timeOfDay is "evening" or "late_night" → frame as "for tonight's session" not "for today"; respect today's accumulated steps + fuelling.
- recovery.wristTempDeltaC > +0.30 AND HRV down >10% → flag illness/infection over fatigue. Bias REST and say so explicitly ("wrist temp +0.4°C above 14d baseline alongside HRV drop reads as illness, not training fatigue").
- recovery.wristTempDeltaC < -0.20 → cooler than baseline (alcohol the night before is a common cause); HRV reading is unreliable, lean on RHR + sleep instead.
- recovery.lastWorkoutHrRecoveryBpm < 12 → cardio recovery is sluggish; cap intensity recommendation at MEDIUM unless HRV is firmly positive.
- recovery.lastWorkoutHrRecoveryBpm ≥ 25 → strong recovery signal; a borderline HARD call can stay HARD.
- activity.walkingHrDeltaBpm ≥ +10 (today's walking HR is ≥10 bpm over the 14-day baseline in activity.walkingHrBaseline14d) without a training change → under-recovered or low-grade illness; bias one tier easier and cite the delta. A persistently NEGATIVE walkingHrTrend3vs14Bpm (≤ −3) is a sign aerobic fitness is improving.
- recovery.vo2Max rising (vo2MaxDelta30d > +0.5) while in a deficit → aerobic base improving and recovery intact; a borderline HARD call can stay HARD.
- recovery.vo2Max falling (vo2MaxDelta30d < -0.5) alongside heavy recent training → deconditioning/overreaching pattern; bias one tier easier and say so.
- recovery.mindfulMinutesToday > 0 → parasympathetic load being managed; supports recovery but is never the deciding factor on its own.

SELF-CORRECTION (use selfFeedback):
- If selfFeedback.userOverrodeYesterday is true (you said REST, they trained), reference it: "You trained through yesterday's REST recommendation — today's HRV/RHR show whether you got away with it."
- If yesterdayRecommendation is HARD and trainedYesterday is false, do NOT count yesterday as a training day in the recovery math.

CITATION RULES — hard requirements, not suggestions:
- If hrv.today is present (non-null, > 0), the rationale MUST cite the value in ms and the deltaPct vs avg14d (e.g. "HRV 38ms, down 14% from 14d avg of 44ms").
- If rhr.today is present (non-null, > 0), the rationale MUST cite bpm and deltaBpm.
- When acwr is present, cite it if it's the deciding factor (e.g. "ACWR 1.7 — load spiked").
- When the last workout's volumeKg or maxHr is the deciding factor, cite the specific number.
- When sleep debt or overnight weight drop is the deciding factor, cite the specific number.
- Only when a value is null/0 may you omit it — and in that case, the rationale should still NOT mention "missing data". Just work with what is present.

PRE-WORKOUT FUEL — these are READINESS factors, not just nutrition:
- nutrition.todayCalories / todayCarbs / todayProtein represent intake SO FAR today.
- Hard sessions need carbs on board. todayCarbs < 30g + > 4h since last meal + fasting = "fuel before training" caveat in the rationale.
- Empty stomach + EASY/MEDIUM classification = mention "fasted training is fine for this intensity" if other markers are good.
- todayProtein 0g going into a strength session → flag the protein gap in nutritionAdvice.

OTHER RULES:
- Lower back pain flag → never recommend heavy spinal loading on EASY or REST days.
- Steve today = true → still output classification but rationale should end with "Tell Steve."
- Use actual numbers — don't speak in generalities.`;

const READINESS_SCHEMA = {
  type: "object",
  properties: {
    classification: { type: "string", enum: ["HARD", "MEDIUM", "EASY", "REST", "RECOVERY"] },
    rationale:      { type: "string" },
    nutritionAdvice: { type: "string" },
    keyDrivers: {
      type: "array",
      items: { type: "string" },
      minItems: 3,
      maxItems: 5,
    },
  },
  required: ["classification", "rationale", "nutritionAdvice", "keyDrivers"],
};

export async function generateReadiness(allData, { steveToday = false } = {}) {
  const profile = getUserProfile();
  const inputs  = computeReadinessInputs(allData);
  publishSources("readiness", readinessSources(inputs));

  // Deterministic 0–100 score + band. This DECIDES the classification; the LLM
  // only narrates it (same hybrid as the AI Calorie Target engine).
  const scored = scoreReadiness(inputs);

  const sys = composeSystem([
    COACH_VOICE,
    EVIDENCE_STANDARD,
    formatProfileForPrompt(profile),
    READINESS_CRITERIA,
    OUTPUT_JSON_STRICT,
    `Task: Explain training readiness.

CLASSIFICATION IS ALREADY DECIDED. If the input contains a "predetermined" object, you MUST return exactly predetermined.classification (a 0–100 readiness score of predetermined.score drives it; predetermined.topDrivers lists the factors that moved it most). Do NOT pick a different classification — your job is to justify and narrate THIS one with the user's numbers, and the RECOVERY override still applies when trainedToday is true. If "predetermined" is absent, fall back to the classification rules below.

RATIONALE FORMAT — return EXACTLY 3-5 short bullets separated by single newlines. NO bullet characters (no •, no -, no *), NO numbering — the UI prefixes them. Each bullet:
  - ≤ 18 words, single sentence
  - cites a specific number (HRV ms, % delta, hours, kcal, etc.)
  - wraps the decisive number / keyword in **double-asterisks** so the UI can bold it for scanning. Example: "Sleep **5h12m** is **1h44m below your 7d avg** — recovery is incomplete."
  - one bullet = one distinct signal (do not pack two signals into one bullet)

RATIONALE CONTENT depends on classification:

▸ When classification is RECOVERY (training.trainedToday is true):
  The user already trained today — DO NOT recommend whether to train. Bullets answer "what for the rest of today + tomorrow's outlook". MUST cover AT LEAST THREE of:
  • Today's session quality — cite training.lastWorkout fields (durationMin, totalVolumeKg, totalSets, prCount, maxHr, avgHr, activeKcal). Was it a hard session by your standards?
  • Post-workout recovery markers — RHR/HRV today vs baseline (how the body is responding now), recovery.lastWorkoutHrRecoveryBpm (if present), wrist temp.
  • Second-session guidance — explicitly green-light or rule out a 2nd session today (and why). Default: no 2nd session unless light cardio/mobility.
  • Tomorrow's outlook — name a likely classification ("Tomorrow likely **MEDIUM** if sleep ≥7h and HRV holds within 5%").
  • Load context — ACWR, consecutiveTrainingDays, 7d sleep debt — flag if today's session pushed any into overreach territory.

▸ When classification is HARD / MEDIUM / EASY / REST (no session yet today):
  Bullets answer "should I train today and how hard". MUST cover AT LEAST THREE distinct signals from across these categories (don't just pick HRV + recovery time every time):
  • Recovery markers: HRV (today + 3d trend), RHR (today + 3d trend), sleep (today vs 7d avg + debt)
  • Apple Health recovery signals: recovery.wristTempDeltaC (illness/alcohol), recovery.vo2Max + vo2MaxDelta30d (aerobic trend), recovery.lastWorkoutHrRecoveryBpm (conditioning), recovery.mindfulMinutesToday, activity.walkingHrAvgBpm (under-recovery) — cite these by their actual value when they materially affect the call
  • Load markers: hours since last session, ACWR, last workout intensity (volumeKg, maxHr, durationMin, prCount), consecutive training days
  • Confounder markers: overnightDeltaKg (hydration), weight7dDeltaKg (deficit pressure), steps48h (passive load), mounjaro dayOfCycle, fasting state, todayCarbs/Protein for pre-workout fuel
  • Self-feedback: if userOverrodeYesterday is true, REFERENCE it

Don't repeat the chip text verbatim — chips are the headline; the bullets tell the story. Always cite specific numbers, not generalities.

KEYDRIVERS: 3-5 short chip labels (1-3 words each), each naming a distinct signal you actually weighed. For RECOVERY, prefer labels like "Session: 12 sets", "Vol 4,820 kg", "Max HR 168", "Tomorrow: MEDIUM?", "HR recov 28". For pre-session classifications: "HRV trend", "Sleep debt", "ACWR 1.1", "53h recovery", "152g carbs", "Steps 18k". Pull from the load + confounder pools, not just HRV/Recovery every time.

NUTRITION ADVICE: Return EXACTLY 2-3 short bullets separated by single newlines. NO bullet characters, NO numbering. Each bullet ≤ 14 words, single sentence, cites a specific number, wraps the decisive number/keyword in **double-asterisks**.

  ▸ When classification is RECOVERY: bullets flip to the post-workout window. Cover the highest-leverage items: protein target within next 90 min (cite grams vs proteinGoal), carb refill if session was heavy/long (cite carbs vs deficit), hydration if overnight weight drop was big, Mounjaro day if applicable. Skip pre-workout topics entirely.
  ▸ Otherwise: pre-workout fuel state — protein floor, pre-workout carbs, Mounjaro cycle day, yesterday's adherence if notable. Skip anything not actionable today.`,
  ]);

  // The band is already decided deterministically — tell the model so its
  // rationale matches the score instead of picking its own classification.
  const payload = {
    ...inputs, steveToday,
    predetermined: scored ? { classification: scored.band, score: scored.score, topDrivers: scored.contributions.slice(0, 3) } : null,
  };

  const raw = await callGemini(JSON.stringify(payload), {
    systemInstruction: sys,
    responseSchema: READINESS_SCHEMA,
    model: MODELS.readiness,
    maxOutputTokens: 2048,
    temperature: 0.3,
    feature: "readiness",
  });

  let parsed;
  try { parsed = JSON.parse(raw); } catch { throw new Error("Could not parse readiness response"); }

  // If the user already trained today, force the classification to RECOVERY
  // regardless of what the model returned. The prompt asks for this but
  // belt-and-braces — old cached prompts or hallucinations shouldn't be
  // able to tell a post-workout user to "go hard".
  const forceRecovery = !!inputs?.training?.trainedToday;
  // Classification comes from the deterministic band (RECOVERY override wins);
  // only fall back to the model's pick if scoring produced nothing.
  const classification = forceRecovery ? "RECOVERY"
    : (scored?.band || parsed.classification || "MEDIUM");
  const result = {
    classification,
    rationale:       parsed.rationale       || "",
    nutritionAdvice: parsed.nutritionAdvice || "",
    keyDrivers:      Array.isArray(parsed.keyDrivers) ? parsed.keyDrivers : [],
    steveToday,
    // Deterministic score block — drives the gauge, the "why" bars and the
    // "what lifts it" levers in the card.
    score:         scored?.score ?? null,
    scoreBand:     scored?.scoreBand ?? null,
    confidence:    scored?.confidence ?? null,
    contributions: scored?.contributions ?? [],
    whatLifts:     scored?.whatLifts ?? [],
    toNextBand:    scored?.toNextBand ?? null,
    gates:         scored?.gates ?? [],
    cappedFromScore: scored?.cappedFromScore ?? false,
    cappedNote:    scored?.cappedNote ?? null,
  };

  saveReadinessCache(result);
  return result;
}
